// React
import React, { useEffect, useRef, useState } from "react";

// Libraries
import PropTypes from "prop-types";
import { Loader, Image } from "semantic-ui-react";
import { isEmpty } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faRotateRight, faPrint } from "@fortawesome/free-solid-svg-icons";
import Lightbox from "yet-another-react-lightbox";
import { Fullscreen, Zoom, Download } from "yet-another-react-lightbox/plugins";

// Redux
import { connect } from "react-redux";
import downloadFile from "@Actions/downloads";

// Utils
import { isRemoteMediaUri } from "@Utils";

// Components
import Text from "@Components/Text";

// Styles
import styles from "./styles.module.css";

const ImageContent = ({ dispatch, item }) => {
	const [isDownloading, setIsDownloading] = useState(false);
	const [fileUri, setFileUri] = useState(null);
	const [rotationAngle, setRotationAngle] = useState(0);
	const [lightbox, setLightbox] = useState({ open: false, selectedImage: null });
	const cachedFileUriRef = useRef(null);

	useEffect(() => {
		const { uri } = item;
		let cancelled = false;

		(async () => {
			if (cachedFileUriRef.current) {
				setFileUri(cachedFileUriRef.current);
				setIsDownloading(false);
				return;
			}

			if (isRemoteMediaUri(uri)) {
				setIsDownloading(true);
				const file = await dispatch(downloadFile({ uri }));
				if (cancelled) return;
				if (file) {
					cachedFileUriRef.current = file;
					setFileUri(file);
				}
				setIsDownloading(false);
				return;
			}

			setFileUri(uri);
			setIsDownloading(false);
		})();

		return () => {
			cancelled = true;
		};
	}, [item.id, item.uri, dispatch]);

	useEffect(() => {
		return () => {
			if (cachedFileUriRef.current) {
				URL.revokeObjectURL(cachedFileUriRef.current);
				cachedFileUriRef.current = null;
			}
		};
	}, [item.id]);

	const handleOpenLightbox = () => {
		setLightbox({ open: true, selectedImage: fileUri });
	};

	const handleCloseLightbox = () => {
		setLightbox({ open: false, selectedImage: null });
	};

	const handleRotateLeft = () => {
		setRotationAngle((prevAngle) => {
			return prevAngle <= 0 ? 270 : prevAngle - 90;
		});
	};

	const handleRotateRight = () => {
		setRotationAngle((prevAngle) => {
			return prevAngle >= 270 ? 0 : prevAngle + 90;
		});
	};

	const slides = (selectedImage) => {
		return [{ src: selectedImage }];
	};

	const handlePrint = () => {
		window.print();

		handleCloseLightbox();
	};

	return (
		<>
			{isDownloading && (
				<div className={styles.imageContainer}>
					<Loader inline active size="large" />
				</div>
			)}
			{!isDownloading && (
				<div className={styles.imageContainer}>
					<Lightbox
						open={lightbox.open}
						plugins={[Fullscreen, Download, Zoom]}
						close={handleCloseLightbox}
						slides={slides(lightbox.selectedImage)}
						zoom={{
							maxZoomPixelRatio: 5,
							zoomInMultiplier: 2,
						}}
						className={`rotated${rotationAngle}`}
						toolbar={{
							buttons: [
								<button key="print" type="button" className="yarl__button" onClick={handlePrint}>
									<FontAwesomeIcon icon={faPrint} size="xl" />
								</button>,
								<button key="rotate-left" type="button" className="yarl__button" onClick={handleRotateLeft}>
									<FontAwesomeIcon icon={faRotateLeft} size="xl" />
								</button>,
								<button key="rotate-right" type="button" className="yarl__button" onClick={handleRotateRight}>
									<FontAwesomeIcon icon={faRotateRight} size="xl" />
								</button>,
								"zoom",
								"download",
								"fullscreen",
								"close",
							],
						}}
					/>
					{fileUri && <Image className={styles.image} src={fileUri} alt="Imagen" onClick={handleOpenLightbox} />}
				</div>
			)}
			{!isEmpty(item.text) && (
				<Text className={styles.text} withMarkdown>
					{item.text}
				</Text>
			)}
		</>
	);
};

ImageContent.propTypes = {
	dispatch: PropTypes.func,
	item: PropTypes.object,
};

export default connect()(ImageContent);
